import { useCallback, useEffect, useMemo } from 'react';
import * as Hublets from 'hubspot-url-utils/hublets-next';
import {
  useOutsideFrameMessenger,
  useInsideFrameMessenger,
  simpleStateSetHandler,
} from './messengerHooks';
import {
  StaticContent,
  StaticWidgetAuthType,
  RenderContextType,
} from '../types';
import { SearchRequestType } from '../../__generated__/chirp/com/hubspot/learning/center/search/rpc/SearchChirpService';
import { LearningContentCardModel } from 'academy-common-lib/features/cards/types';
import { HubError } from 'academy-common-lib/utils/DataFetchingClient';
import { ViewProps } from 'foundations-components/transitional/components/View';
import { CardGridProps } from 'foundations-components/transitional/components/CardGrid';
import { OnProgressProps } from 'academy-common-lib/components/video/ACVideoPlayer';
import type { TextProps } from 'foundations-components/components/Text';
import type { StyleProps } from 'foundations-components/types';

export interface StaticState {
  authType?: StaticWidgetAuthType;
  autoPlay?: boolean;
  Title?: React.ElementType;
  Description?: React.ElementType;
  lessonId?: number;
  mutedByDefault?: boolean;
  contentId?: number;
  shortId?: number;
  language?: string;
  variant?: 'default' | 'video';
  startTime?: string;
  sectionEndTime?: string;
  enablePictureInPicture?: boolean;
  renderContext?: RenderContextType;
  hublet?: keyof typeof Hublets;
  linkTextProps?: Partial<TextProps>;
  videoWrapperProps?: StyleProps;
  openLinksInNewTab?: boolean;
}

const staticStateMessageKey = 'staticState' as const;

const contentMessageKey = 'content' as const;
const errorMessageKey = 'error' as const;
const noContentMessageKey = 'noContent' as const;
const videoPlayMessageKey = 'videoPlay' as const;
const videoFinishMessageKey = 'videoFinish' as const;
const videoEngagementMessageKey = 'videoEngagement' as const;
const videoProgressMessageKey = 'videoProgress' as const;
const watermarkLinkClickMessageKey = 'watermarkLinkClick' as const;

export type WatermarkLinkType = 'learn-by-doing' | 'watch-on-academy';

export interface WatermarkClick {
  linkType: WatermarkLinkType;
  href?: string;
}

// Recommendations Widget keys
const contentErrorMessageKey = 'contentError' as const;
const cardClickMessageKey = 'cardClick' as const;
const contentLoadMessageKey = 'contentLoad' as const;

export const useSyncStaticState = ({
  newState,
  onContent,
  onError,
  onNoContent,
  onVideoPlay,
  onVideoFinish,
  onVideoEngagement,
  onVideoProgress,
  onWatermarkClick,
}: {
  newState: StaticState;
  onContent?: (content: any) => void;
  onError?: () => void;
  onNoContent?: () => void;
  onVideoPlay?: () => void;
  onVideoFinish?: () => void;
  onVideoEngagement?: () => void;
  onVideoProgress?: (progress: OnProgressProps) => void;
  onWatermarkClick?: (watermarkClick: WatermarkClick) => void;
}) => {
  const messageHandlers = useMemo(
    () => ({
      [contentMessageKey]: (message: StaticContent) => {
        onContent?.(message);
      },
      [errorMessageKey]: () => {
        onError?.();
      },
      [noContentMessageKey]: () => {
        onNoContent?.();
      },
      [videoPlayMessageKey]: () => {
        onVideoPlay?.();
      },
      [videoFinishMessageKey]: () => {
        onVideoFinish?.();
      },
      [videoEngagementMessageKey]: () => {
        onVideoEngagement?.();
      },
      [videoProgressMessageKey]: (progress: OnProgressProps) => {
        onVideoProgress?.(progress);
      },
      [watermarkLinkClickMessageKey]: (watermarkClick: WatermarkClick) => {
        onWatermarkClick?.(watermarkClick);
      },
    }),
    [
      onContent,
      onError,
      onNoContent,
      onVideoEngagement,
      onVideoFinish,
      onVideoPlay,
      onVideoProgress,
      onWatermarkClick,
    ]
  );

  const { registerIFrame, sendMessage, isReady } =
    useOutsideFrameMessenger<StaticState>({
      key: 'static',
      messageHandlers,
    });

  useEffect(() => {
    if (newState && sendMessage && isReady) {
      sendMessage(staticStateMessageKey, newState);
    }
  }, [isReady, newState, sendMessage]);

  return { registerIFrame };
};

const syncStaticInsideHandlers = {
  [staticStateMessageKey]: simpleStateSetHandler,
};

export const useSyncStaticStateInsideIFrame = () => {
  const { state, sendMessage } = useInsideFrameMessenger<StaticState>({
    key: 'static',
    messageHandlers: syncStaticInsideHandlers,
  });

  const onContent = useCallback(
    (content: StaticContent) => {
      sendMessage(contentMessageKey, content);
    },
    [sendMessage]
  );

  const onError = useCallback(() => {
    sendMessage(errorMessageKey, {});
  }, [sendMessage]);

  const onNoContent = useCallback(() => {
    sendMessage(noContentMessageKey, {});
  }, [sendMessage]);

  const onVideoPlay = useCallback(() => {
    sendMessage(videoPlayMessageKey, {});
  }, [sendMessage]);

  const onVideoFinish = useCallback(() => {
    sendMessage(videoFinishMessageKey, {});
  }, [sendMessage]);

  const onVideoEngagement = useCallback(() => {
    sendMessage(videoEngagementMessageKey, {});
  }, [sendMessage]);

  const onVideoProgress = useCallback(
    (progress: OnProgressProps) => {
      sendMessage(videoProgressMessageKey, progress);
    },
    [sendMessage]
  );

  const onWatermarkClick = useCallback(
    ({ linkType, href }: WatermarkClick) => {
      sendMessage(watermarkLinkClickMessageKey, { linkType, href });
    },
    [sendMessage]
  );

  return {
    state,
    onContent,
    onError,
    onNoContent,
    onVideoPlay,
    onVideoFinish,
    onVideoEngagement,
    onVideoProgress,
    onWatermarkClick,
  };
};

export type SearchOptions = Pick<
  SearchRequestType,
  'languages' | 'search' | 'categories' | 'contentTypes'
>;

export type RecommendationsProps = {
  header?: string;
  maxCards?: number;
  searchOptions?: SearchOptions;
  type: 'ml' | 'search';
  containerProps?: ViewProps;
  responsive?: boolean;
  cardGridProps?: CardGridProps;
};

export type UseSyncRecommendationsProps = {
  props: RecommendationsProps;
  onCardClick?: (card: LearningContentCardModel) => void;
  onContentError?: (error: HubError) => void;
  onContentLoad?: (content: LearningContentCardModel[]) => void;
};

export const useSyncRecommendationsProps = ({
  props,
  onCardClick,
  onContentError,
  onContentLoad,
}: UseSyncRecommendationsProps) => {
  const messageHandlers = useMemo(
    () => ({
      [cardClickMessageKey]: (card: LearningContentCardModel) => {
        onCardClick?.(card);
      },
      [contentLoadMessageKey]: (content: LearningContentCardModel[]) => {
        onContentLoad?.(content);
      },
      [contentErrorMessageKey]: (error: HubError) => {
        onContentError?.(error);
      },
    }),
    [onContentLoad, onCardClick, onContentError]
  );

  const { registerIFrame, sendMessage, isReady } =
    useOutsideFrameMessenger<RecommendationsProps>({
      key: 'static',
      messageHandlers,
    });

  useEffect(() => {
    if (isReady) {
      sendMessage?.(staticStateMessageKey, props);
    }
  }, [isReady, sendMessage, props]);

  return {
    registerIFrame,
    sendMessage,
    isReady,
  };
};

export const useSyncRecommendationsPropsInsideIFrame = () => {
  const { state: props, sendMessage } =
    useInsideFrameMessenger<RecommendationsProps>({
      key: 'static',
      messageHandlers: syncStaticInsideHandlers,
    });

  const onCardClick = useCallback(
    (card: LearningContentCardModel) => {
      sendMessage(cardClickMessageKey, card);
    },
    [sendMessage]
  );

  const onContentLoad = useCallback(
    (content: LearningContentCardModel[]) => {
      sendMessage(contentLoadMessageKey, content);
    },
    [sendMessage]
  );

  const onContentError = useCallback(
    (error: HubError) => {
      sendMessage(contentErrorMessageKey, error);
    },
    [sendMessage]
  );

  return {
    props,
    sendMessage,
    onCardClick,
    onContentLoad,
    onContentError,
  };
};
